import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  useTVEventHandler,
  Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import { ScreenFooter } from "../components/ScreenFooter";
import { ErrorScreen } from "./ErrorScreen";
import { BackgroundGradient } from "../components/BackgroundGradient";
import { useActivePlaylist } from "../providers/PlaylistProvider";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";

const makeStyles = (theme: ThemeOptions) =>
  StyleSheet.create({
    wrapper: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
    },
    button: {
      paddingHorizontal: theme.spacing.large,
      paddingVertical: theme.spacing.medium,
      backgroundColor: theme.accent,
      borderRadius: theme.borderRadius.medium,
      borderWidth: theme.borderWidth.large,
    },
    buttonText: {
      fontSize: theme.fontSize.large,
      textTransform: "uppercase",
      color: theme.accentText,
    },
  });

export const InteractionScreen = (props: { onInteraction: () => void }) => {
  const { playlist } = useActivePlaylist();
  const theme = useTheme();
  const styles = makeStyles(theme);

  const { t } = useTranslation("InteractionScreen");
  const [hovering, setHovering] = useState(false);
  const [pressing, setPressing] = useState(false);

  useEffect(() => {
    if (playlist.length === 0) {
      return;
    }

    if (Platform.OS === "web") {
      window.addEventListener("mousedown", props.onInteraction);
      window.addEventListener("keypress", props.onInteraction);
      return () => {
        window.removeEventListener("mousedown", props.onInteraction);
        window.removeEventListener("keypress", props.onInteraction);
      };
    }
  }, [playlist, props.onInteraction]);

  if (playlist.length === 0) {
    return (
      <ErrorScreen
        message={t("No configurations assigned")}
        subMessages={[
          t(
            "You can assign a configuration or a playlist to run on this device in the Digital Displays management app.",
          ),
        ]}
      />
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <BackgroundGradient background={theme.background} accent={theme.accent} />
      <View style={styles.wrapper}>
        {Platform.OS !== "web" && (
          <TVListener onInteraction={props.onInteraction} />
        )}
        <Pressable
          onHoverIn={() => setHovering(true)}
          onHoverOut={() => setHovering(false)}
          onPressIn={() => setPressing(true)}
          onPressOut={() => setPressing(false)}
          hasTVPreferredFocus
          style={[
            styles.button,
            {
              borderColor: theme.toRgba(
                theme.contrastColor,
                hovering || pressing ? 0.3 : 0,
              ),
            },
          ]}
        >
          <Text style={[styles.buttonText]}>{t("Press any key to begin")}</Text>
        </Pressable>
      </View>

      <ScreenFooter />
    </View>
  );
};

const TVListener = (props: { onInteraction: () => void }) => {
  useTVEventHandler((ev) => {
    if (ev.eventType !== "focus") {
      props.onInteraction();
    }
  });
  return null;
};
